import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addCardToQueue, addCardToQueueDisplay, updateCardDataQuantity, invalidateCollection } from '../../../../../../store/card/card.action';
import { removeCardFromFirestore } from '../../../../../../utils/firebase/firebase.utils';
import { addCardToWhatnot } from '../../../../../../utils/firebase/whatnot';
import { getWhatnotPrefs } from '../../../../../../hooks/useWhatnotPrefs';
import { addCardToEbay } from '../../../../../../utils/firebase/ebay';
import { addCardToDiscord } from '../../../../../../utils/firebase/discordQueue';
import { getEbayPrefs } from '../../../../../../hooks/useEbayPrefs';
import { selectCardCount, selectUser } from '../../../../../../store/user/user.selector';
import { setSystemNotification } from '../../../../../../store/system/system.action';
import { setIsViewingNewSelection, setNewSelectionCardData } from '../../../../../../store/showcase/showcase.action';
import CardInfoModal from './components/card-info-modal/CardInfoModal.js';
import { buildEbaySearchUrl as buildEbaySearchUrlUtil } from '../../../../../../utils/ebay/buildEbaySearchUrl';
import { normalizeForCompare } from '../../../../../../utils/textNormalization';

// styles
import {
    ChecklistItemPanelContainer,
    LeftContainer,
    RightContainer,
    CardNumber,
    DetailsContainer,
    Name,
    TagsContainer,
    QtyBtn,
    QtyControlsGroup,
    QtyInputWrapper,
    QtyInputContainer,
    QtyInputField,
    QtyInputLabel,
    CustomInputsRow,
    CustomInputWrapper,
} from './ChecklistItemPanel.styles.js';

// components
import ParallelAttribute from './components/parallel-attribute/ParallelAttribute.js';
import InputDropdownSearchable from '../../../../../../components/inputs/input-dropdown-searchable/InputDropdownSearchable.js';
import InputBar from '../../../../../../components/input-bar/InputBar.js';

const QtyDisplay = memo(function QtyDisplay({ value, onClick }) {
    const label = value < 0 ? 'REMOVE' : 'ADD';
    const displayValue = Math.abs(value);
    return (
        <QtyInputWrapper onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClick?.(e)}>
            <QtyInputContainer>
                <QtyInputLabel>{label}</QtyInputLabel>
                <QtyInputField>{displayValue}</QtyInputField>
            </QtyInputContainer>
        </QtyInputWrapper>
    );
});

function normalizeAttributes(attrs) {
    if (!attrs?.length) return [];
    return attrs.map((a) =>
        typeof a === 'string' ? { text: a, color: '#FFFFFF' } : { text: a.text ?? a.name, color: a.color ?? '#FFFFFF', isAlwaysExpanded: a.isAlwaysExpanded }
    );
}

function toTitleCase(str) {
    if (!str || typeof str !== 'string') return str;
    return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeParallels(parallels) {
    if (!parallels?.length) return [];
    return parallels.map((p) =>
        typeof p === 'string' ? { text: p, color: '#FFFFFF' } : { text: p.text ?? p.name, color: p.color ?? '#FFFFFF' }
    );
}

function getCollectedParallelDisplayText(collectedCard) {
    const hasSerial = collectedCard?.cardPrintNumber != null;
    return hasSerial
        ? `${collectedCard.cardPrintNumber}/${collectedCard?.cardSupply ?? 1}`
        : `x${collectedCard?.cardQty ?? 1}`;
}

/** Build display text for custom parallel preview: "Name" or "Name #X/Y" like the card modal. */
function getCustomPreviewDisplayText(name, cardNum, customSupply) {
    const namePart = (name && String(name).trim()) || 'Custom';
    if (cardNum != null && customSupply != null) return `${namePart} #${cardNum}/${customSupply}`;
    if (cardNum != null) return `${namePart} #${cardNum}`;
    if (customSupply != null) return `${namePart} /${customSupply}`;
    return namePart;
}

/** Ensure color string has a leading # for hex (e.g. "FFFFFF" → "#FFFFFF"). */
function normalizeHexColor(value) {
    if (value == null || value === '') return '#FFFFFF';
    const s = String(value).trim();
    return s.startsWith('#') ? s : `#${s}`;
}

/** Parse serial input to get print number (X). Accepts "5", "5/99", or number. Returns null if empty/invalid. */
function getSerialPrintNumber(value) {
    if (value === '' || value == null) return null;
    const n = parseInt(String(value).trim(), 10);
    return !Number.isNaN(n) && n > 0 ? n : null;
}

function normalizeParallelDataForModal(parallelData) {
    if (!parallelData?.length) return [];
    return parallelData.map((p) =>
        typeof p === 'string'
            ? { name: p, color: '#FFFFFF', limitedTo: null, odds: null }
            : { name: p.name ?? p.text ?? '', color: p.color ?? '#FFFFFF', limitedTo: p.limitedTo ?? null, odds: p.odds ?? null }
    );
}

function ChecklistItemPanelInner({ card, parallels: parallelsProp, parallelData = [], parallelOptions = [], defaultParallel = 'Base', mirrored = false }) {
    const { sport, year, manufacturer, series, cardType, cardTypeCategory } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const cardCount = useSelector(selectCardCount);

    const attributes = normalizeAttributes(card?.attributes ?? card?.cardAttributes);
    const parallels = normalizeParallels(parallelsProp ?? card?.parallels ?? []);
    const players = card?.players ?? card?.name ?? [];
    const parallelsForModal = normalizeParallelDataForModal(parallelData);
    const parallelsWithLimitedTo = parallelsForModal;

    const rawOptions = parallelOptions.length > 0
        ? parallelOptions
        : parallels.map((p) => p.text);
    const filtered = rawOptions.filter(Boolean);
    const rest = filtered
        .filter((o) => String(o).toUpperCase() !== 'BASE')
        .map(toTitleCase);

    /* Print runs keyed by name so the dropdown can label options "Gold Refractor /99"
       while still committing the bare name the rest of the panel matches on. */
    const printRunByParallel = useMemo(() => {
        const map = new Map();
        (parallelData ?? []).forEach((p) => {
            if (typeof p === 'string' || !p) return;
            const name = p.name ?? p.text;
            if (name && p.limitedTo != null) map.set(normalizeForCompare(name), p.limitedTo);
        });
        return map;
    }, [parallelData]);

    const dropdownOptions = ['CUSTOM', 'Base', ...rest].map((name) => {
        const printRun = printRunByParallel.get(normalizeForCompare(name));
        return printRun != null
            ? { value: name, label: `${name} /${printRun}` }
            : { value: name, label: name };
    });

    const [qty, setQty] = useState(1);
    const [selectedParallel, setSelectedParallel] = useState(defaultParallel);
    const [serialPrintNumber, setSerialPrintNumber] = useState('');
    const [customSerialSupply, setCustomSerialSupply] = useState('');
    const [customParallelName, setCustomParallelName] = useState('');
    const [customColor, setCustomColor] = useState('#FFFFFF');
    const [showCardInfoModal, setShowCardInfoModal] = useState(false);

    const isCustomSelected = String(selectedParallel).toUpperCase() === 'CUSTOM';
    const selectedParallelObj = useMemo(() => {
        if (isCustomSelected) {
            return { name: customParallelName.trim() || 'Custom', color: customColor, limitedTo: null };
        }
        return parallelsWithLimitedTo.find(
            (p) => String(p.name).toUpperCase() === String(selectedParallel).toUpperCase()
        ) ?? null;
    }, [isCustomSelected, selectedParallel, customParallelName, customColor, parallelsWithLimitedTo]);
    const isSerialNumbered = !isCustomSelected && selectedParallelObj?.limitedTo != null;
    const printRun = selectedParallelObj?.limitedTo ?? null;
    const cardNum = getSerialPrintNumber(serialPrintNumber);
    const customSupply = getSerialPrintNumber(customSerialSupply);
    const serialSupply = isSerialNumbered ? (printRun ?? 1) : (isCustomSelected ? (customSupply ?? null) : null);

    const collectedQtyForSelected = useMemo(() => {
        const collectedCards = card?.collectedCards ?? [];
        if (String(selectedParallel).toUpperCase() === 'BASE') {
            const baseVariation = collectedCards.find((c) => String(c.cardParallelName).toUpperCase() === 'BASE');
            return baseVariation?.cardQty ?? 0;
        }
        if (isCustomSelected) {
            const name = customParallelName.trim();
            if (!name) return 0;
            const variation = collectedCards.find((c) => {
                if (String(c.cardParallelName).toUpperCase() !== name.toUpperCase()) return false;
                if (cardNum != null && c.cardPrintNumber !== cardNum) return false;
                if (customSupply != null && c.cardSupply !== customSupply) return false;
                return true;
            });
            return variation?.cardQty ?? 0;
        }
        const parallelName = selectedParallelObj?.name;
        if (!parallelName) return 0;
        const matchBySerial = cardNum != null && isSerialNumbered;
        const variation = matchBySerial
            ? collectedCards.find((c) => String(c.cardParallelName).toUpperCase() === String(parallelName).toUpperCase() && c.cardPrintNumber === cardNum)
            : collectedCards.find((c) => String(c.cardParallelName).toUpperCase() === String(parallelName).toUpperCase());
        return variation?.cardQty ?? 0;
    }, [card?.collectedCards, selectedParallel, selectedParallelObj, isCustomSelected, customParallelName, cardNum, customSupply, isSerialNumbered]);

    const buildEbaySearchUrl = useCallback(
        (card, parallel) => buildEbaySearchUrlUtil(card, parallel, series),
        [series]
    );

    const dispatchCardToQueue = useCallback((cardData, qty = 1) => {
        dispatch(addCardToQueue(sport, year, manufacturer, series, cardType, cardTypeCategory, cardData, qty));
        dispatch(addCardToQueueDisplay(sport, year, manufacturer, series, cardType, cardTypeCategory, cardData, qty));
    }, [dispatch, sport, year, manufacturer, series, cardType, cardTypeCategory]);

    const handleRemoveCard = useCallback(async (cardParam, parallel, amount) => {
        try {
            const result = await removeCardFromFirestore(user?.uid, parallel, amount, sport, year, manufacturer, series, cardType, cardTypeCategory, cardParam?.cardNumber, cardCount, cardParam);
            if (result?.success) {
                dispatch(updateCardDataQuantity(cardParam?.cardNumber, parallel, amount));
                dispatch(invalidateCollection());
                const message = amount === 1 ? 'Card removed successfully' : `${amount} cards removed successfully`;
                dispatch(setSystemNotification(message));
            }
        } catch (error) {
            dispatch(setSystemNotification('Failed to remove card'));
        }
    }, [user?.uid, sport, year, manufacturer, series, cardType, cardTypeCategory, cardCount, dispatch]);

    const handleAddToWhatnot = useCallback(async (cardParam, variation) => {
        try {
            await addCardToWhatnot(
                user?.uid,
                cardParam,
                variation,
                { sport, year, manufacturer, series, cardType, cardTypeCategory },
                getWhatnotPrefs()
            );
            dispatch(setSystemNotification('Card added to Whatnot'));
        } catch (error) {
            dispatch(setSystemNotification('Failed to add card to Whatnot'));
        }
    }, [user?.uid, sport, year, manufacturer, series, cardType, cardTypeCategory, dispatch]);

    const handleAddToEbay = useCallback(async (cardParam, variation) => {
        try {
            await addCardToEbay(
                user?.uid,
                cardParam,
                variation,
                { sport, year, manufacturer, series, cardType, cardTypeCategory },
                getEbayPrefs()
            );
            dispatch(setSystemNotification('Card added to eBay'));
        } catch (error) {
            dispatch(setSystemNotification('Failed to add card to eBay'));
        }
    }, [user?.uid, sport, year, manufacturer, series, cardType, cardTypeCategory, dispatch]);

    const handleAddToDiscord = useCallback(async (cardParam, variation) => {
        try {
            await addCardToDiscord(
                user?.uid,
                cardParam,
                variation,
                { sport, year, manufacturer, series, cardType, cardTypeCategory }
            );
            dispatch(setSystemNotification('Card added to Discord'));
        } catch (error) {
            dispatch(setSystemNotification('Failed to add card to Discord'));
        }
    }, [user?.uid, sport, year, manufacturer, series, cardType, cardTypeCategory, dispatch]);

    const handleAddToShowcase = useCallback((cardParam, variation) => {
        dispatch(setIsViewingNewSelection(true));
        dispatch(setNewSelectionCardData({
            cardAttributes: cardParam.cardAttributes,
            cardNumber: cardParam.cardNumber,
            name: cardParam.name,
            team: cardParam.team,
            cardPath: [sport, year, manufacturer, series, cardType, cardTypeCategory],
            cardParallel: variation,
        }));
        navigate('/selection-view');
    }, [dispatch, navigate, sport, year, manufacturer, series, cardType, cardTypeCategory]);

    const handleClick = () => setShowCardInfoModal(true);

    const handleAddToQueue = useCallback(() => {
        if (qty > 0) {
            if (selectedParallel.toUpperCase() === 'CUSTOM') {
                const name = customParallelName.trim();
                if (!name) {
                    dispatch(setSystemNotification('Enter a parallel name for custom'));
                    return;
                }
                const hasSerialNum = cardNum != null;
                const hasSupply = customSupply != null;
                if (hasSerialNum !== hasSupply) {
                    dispatch(setSystemNotification('Enter both # and / for serial (X/Y), or leave both empty'));
                    return;
                }
                const parallelCard = {
                    ...card,
                    cardAttributes: card?.cardAttributes ?? [],
                    collectedCards: [{
                        cardParallelName: name,
                        cardColor: customColor.startsWith('#') ? customColor : `#${customColor}`,
                        cardQty: 1,
                        cardPrintNumber: cardNum,
                        cardSupply: customSupply ?? null,
                        cardOdds: null
                    }]
                };
                dispatchCardToQueue(parallelCard, qty);
            } else if (selectedParallel.toUpperCase() === 'BASE') {
                const basePara = parallelsWithLimitedTo.find(
                    (p) => String(p.name).toUpperCase() === 'BASE'
                );
                const baseCard = {
                    ...card,
                    cardAttributes: card?.cardAttributes ?? [],
                    collectedCards: [{
                        cardParallelName: 'BASE',
                        cardColor: '#FFFFFF',
                        cardSupply: null,
                        cardPrintNumber: null,
                        cardOdds: basePara?.odds ?? null
                    }]
                };
                dispatchCardToQueue(baseCard, qty);
            } else {
                const parallel = parallelsWithLimitedTo.find(
                    (p) => String(p.name).toUpperCase() === String(selectedParallel).toUpperCase()
                );
                if (!parallel) return;
                if (isSerialNumbered) {
                    if (cardNum == null || cardNum < 1 || cardNum > (printRun ?? 0)) return;
                }
                const parallelCard = {
                    ...card,
                    cardAttributes: card?.cardAttributes ?? [],
                    collectedCards: [{
                        cardParallelName: parallel.name,
                        cardColor: parallel.color ?? '#FFFFFF',
                        cardQty: 1,
                        cardPrintNumber: isSerialNumbered ? cardNum : null,
                        cardSupply: parallel.limitedTo ?? null,
                        cardOdds: parallel.odds ?? null
                    }]
                };
                dispatchCardToQueue(parallelCard, qty);
            }
            dispatch(setSystemNotification(`Added ${qty} card${qty > 1 ? 's' : ''} to queue`));
            setQty(1);
            if (isSerialNumbered && printRun != null) setSerialPrintNumber('');
        } else {
            const absQty = Math.abs(qty);
            const collectedCards = card?.collectedCards ?? [];
            if (selectedParallel.toUpperCase() === 'BASE') {
                const baseVariation = collectedCards.find((c) => String(c.cardParallelName).toUpperCase() === 'BASE');
                if (baseVariation) {
                    handleRemoveCard(card, 'BASE', Math.min(absQty, baseVariation.cardQty || 1));
                }
            } else if (selectedParallel.toUpperCase() === 'CUSTOM') {
                const name = customParallelName.trim();
                if (!name) {
                    dispatch(setSystemNotification('Enter the parallel name to remove'));
                    return;
                }
                const variation = collectedCards.find((c) => {
                    if (String(c.cardParallelName).toUpperCase() !== name.toUpperCase()) return false;
                    if (cardNum != null && c.cardPrintNumber !== cardNum) return false;
                    if (customSupply != null && c.cardSupply !== customSupply) return false;
                    return true;
                });
                if (variation) {
                    handleRemoveCard(card, variation, Math.min(absQty, variation.cardQty || 1));
                } else {
                    dispatch(setSystemNotification('No matching custom parallel found'));
                }
            } else {
                const parallelName = selectedParallelObj?.name ?? selectedParallel;
                const matchBySerial = cardNum != null && isSerialNumbered;
                const variation = matchBySerial
                    ? collectedCards.find((c) =>
                        String(c.cardParallelName).toUpperCase() === String(parallelName).toUpperCase() &&
                        c.cardPrintNumber === cardNum
                    )
                    : collectedCards.find((c) =>
                        String(c.cardParallelName).toUpperCase() === String(parallelName).toUpperCase()
                    );
                if (variation) {
                    handleRemoveCard(card, parallelName, Math.min(absQty, variation.cardQty || 1));
                }
            }
            setQty(1);
        }
    }, [card, qty, selectedParallel, selectedParallelObj, isSerialNumbered, cardNum, customSupply, printRun, parallelsWithLimitedTo, dispatchCardToQueue, handleRemoveCard, dispatch, customParallelName, customColor]);

    useEffect(() => {
        if (parallelOptions.length > 0) {
            setSelectedParallel((prev) =>
                parallelOptions.includes(prev) || prev === 'CUSTOM' ? prev : (parallelOptions[0] ?? 'Base')
            );
        }
    }, [parallelOptions]);

    useEffect(() => {
        setSelectedParallel(defaultParallel);
        setQty(1);
        setSerialPrintNumber('');
    }, [defaultParallel]);

    useEffect(() => {
        if (qty < 0 && -qty > collectedQtyForSelected) {
            setQty(collectedQtyForSelected > 0 ? -collectedQtyForSelected : 1);
        }
    }, [collectedQtyForSelected, qty]);

    const showSerialInput = isSerialNumbered || isCustomSelected;
    useEffect(() => {
        if (!showSerialInput) {
            setSerialPrintNumber('');
            setCustomSerialSupply('');
        }
    }, [showSerialInput]);

    const customSerialHasValue = isCustomSelected && (
        String(serialPrintNumber ?? '').trim() !== '' || String(customSerialSupply ?? '').trim() !== ''
    );
    const hideQtyButtons = isSerialNumbered || customSerialHasValue;

    useEffect(() => {
        if (hideQtyButtons && qty !== 1) {
            setQty(1);
        }
    }, [hideQtyButtons, qty]);

    const isCollected = (card?.collectedCards?.length ?? 0) > 0;

    return (
        <ChecklistItemPanelContainer $isCollected={isCollected} $mirrored={mirrored} onClick={handleClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleClick()}>
            <LeftContainer $mirrored={mirrored}>
                <DetailsContainer $mirrored={mirrored}>
                    <CardNumber>{card?.cardNumber ?? ''}</CardNumber>
                    <TagsContainer $mirrored={mirrored}>
                        {isCustomSelected ? (
                            <ParallelAttribute
                                key="custom-preview"
                                color={normalizeHexColor(customColor)}
                                text={getCustomPreviewDisplayText(customParallelName, cardNum, customSupply)}
                                isAlwaysExpanded={true}
                            />
                        ) : (
                            <>
                                {attributes.map((attr, i) => (
                                    <ParallelAttribute
                                        key={i}
                                        color={attr.color}
                                        text={attr.text}
                                        isAlwaysExpanded={true}
                                    />
                                ))}
                                {(card?.collectedCards ?? []).map((collected, i) => {
                                    const parallelName = toTitleCase(collected.cardParallelName ?? '');
                                    const displayText = getCollectedParallelDisplayText(collected);
                                    return (
                                        <ParallelAttribute
                                            key={`${collected.cardParallelName}-${collected.cardPrintNumber ?? i}`}
                                            color={collected.cardColor ?? '#FFFFFF'}
                                            text={`${parallelName} ${displayText}`}
                                            title={parallelName}
                                            isAlwaysExpanded={true}
                                            actionLabel="REMOVE x1"
                                            onAction={() => handleRemoveCard(card, collected, 1)}
                                        />
                                    );
                                })}
                            </>
                        )}
                    </TagsContainer>
                </DetailsContainer>
                <Name $mirrored={mirrored}>{players.join(', ')}</Name>
            </LeftContainer>
            <RightContainer $mirrored={mirrored} onClick={(e) => e.stopPropagation()}>
                <InputDropdownSearchable
                    options={dropdownOptions}
                    onChange={setSelectedParallel}
                    value={selectedParallel}
                    id={`parallel-select-${card?.cardNumber ?? ''}`}
                    label="Parallel"
                    searchPlaceholder='e.g. "Base", "/99"...'
                />
                {isCustomSelected && (
                    <CustomInputsRow>
                        <CustomInputWrapper $width="60px">
                            <InputBar
                                type="number"
                                min={1}
                                value={serialPrintNumber}
                                updateValue={setSerialPrintNumber}
                                id={`serial-num-${card?.cardNumber ?? ''}`}
                                label="#"
                                placeholder="—"
                                expands={true}
                            />
                        </CustomInputWrapper>
                        <CustomInputWrapper $width="60px">
                            <InputBar
                                type="number"
                                min={1}
                                value={customSerialSupply}
                                updateValue={setCustomSerialSupply}
                                id={`serial-supply-${card?.cardNumber ?? ''}`}
                                label="/"
                                placeholder="—"
                                expands={true}
                            />
                        </CustomInputWrapper>
                        <CustomInputWrapper $width="100px">
                            <InputBar
                                type="text"
                                value={customParallelName}
                                updateValue={setCustomParallelName}
                                id={`custom-name-${card?.cardNumber ?? ''}`}
                                label="Name"
                                expands={true}
                            />
                        </CustomInputWrapper>
                        <CustomInputWrapper $width="88px">
                            <InputBar
                                type="text"
                                value={customColor}
                                updateValue={setCustomColor}
                                id={`custom-color-${card?.cardNumber ?? ''}`}
                                label="Color"
                                placeholder="#FFFFFF"
                                expands={true}
                            />
                        </CustomInputWrapper>
                    </CustomInputsRow>
                )}
                {isSerialNumbered && (
                    <CustomInputWrapper $width="80px">
                        <InputBar
                            type="number"
                            min={1}
                            max={printRun ?? undefined}
                            value={serialPrintNumber}
                            updateValue={setSerialPrintNumber}
                            id={`serial-input-${card?.cardNumber ?? ''}`}
                            label={serialSupply != null ? `# / ${serialSupply}` : '#'}
                            placeholder={`1-${printRun ?? '?'}`}
                            expands={true}
                        />
                    </CustomInputWrapper>
                )}
                <QtyControlsGroup>
                    {!hideQtyButtons && (
                        <QtyBtn
                            onClick={(e) => {
                                e.stopPropagation();
                                setQty((n) => {
                                    if (n === 1) {
                                        if (collectedQtyForSelected <= 0) return n;
                                        return -1;
                                    }
                                    const minQty = -collectedQtyForSelected;
                                    return Math.max(n - 1, minQty);
                                });
                            }}
                        >
                            -
                        </QtyBtn>
                    )}
                    <QtyDisplay value={qty} onClick={(e) => { e.stopPropagation(); handleAddToQueue(); }} />
                    {!hideQtyButtons && (
                        <QtyBtn onClick={(e) => { e.stopPropagation(); setQty((n) => (n === -1 ? 1 : n + 1)); }}>+</QtyBtn>
                    )}
                </QtyControlsGroup>
            </RightContainer>
            <CardInfoModal
                isOpen={showCardInfoModal}
                onClose={() => setShowCardInfoModal(false)}
                card={card}
                year={year}
                manufacturer={manufacturer}
                series={series}
                buildEbaySearchUrl={buildEbaySearchUrl}
                parallels={parallelsForModal}
                sport={sport}
                cardType={cardType}
                cardTypeCategory={cardTypeCategory}
                dispatchCardToQueue={dispatchCardToQueue}
                handleRemoveCard={handleRemoveCard}
                handleAddToWhatnot={handleAddToWhatnot}
                handleAddToEbay={handleAddToEbay}
                handleAddToDiscord={handleAddToDiscord}
                handleAddToShowcase={handleAddToShowcase}
                showcaseView={false}
            />
        </ChecklistItemPanelContainer>
    );
}

const ChecklistItemPanel = memo(ChecklistItemPanelInner);
export default ChecklistItemPanel;
